import { useEffect, useMemo } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { PAYMENT_METHOD } from 'lib/core/resources/constants'
import { PaymentToken } from 'lib/core/data/customer/paymentTokens/types'
import { useCustomerPaymentTokens } from 'lib/core/hooks'
import {
  payeezyPaymentTokenList,
  PayeezyPaymentToken,
  payeezyPaymentTokenSave,
  payeezyPaymentTokenDelete,
  PayeezyPaymentTokenDeleteOut,
  parseCreditCardDetails,
  PayeezyCreditCardMeta
} from '../utils'

export type PayeezyTokenList = Partial<Record<string, PayeezyPaymentToken>>

export interface PayeezyTokens {
  vault: PaymentToken
  payeezy: PayeezyPaymentToken
  meta: PayeezyCreditCardMeta
}

export const usePayeezyTokens = () => {
  const { fetchPaymentTokens, paymentTokens, isFetching: vaultTokensLoading } = useCustomerPaymentTokens(
    {
      paymentMethodCode: PAYMENT_METHOD.PAYEEZY
    }
  )

  const { data: tokens, loading: tokensLoading, refetch } = useQuery<{
    payeezyPaymentTokenList: PayeezyPaymentToken[]
  }>(payeezyPaymentTokenList, {
    fetchPolicy: 'no-cache'
  })

  const [savePaymentToken, { loading: tokenIsSaving }] = useMutation<{
    payeezyPaymentTokenSave: { entity_id: number }
  }>(payeezyPaymentTokenSave)

  const [deletePaymentToken, { loading: tokenIsDeleting }] = useMutation<{
    payeezyPaymentTokenDelete: PayeezyPaymentTokenDeleteOut
  }>(payeezyPaymentTokenDelete)

  const payeezyTokenList = useMemo(() => {
    return tokens?.payeezyPaymentTokenList.reduce((index: PayeezyTokenList, item) => {
      const hash = item.public_hash
      index[hash] = item
      return index
    }, {})
  }, [tokens])

  const payeezyTokens: PayeezyTokens[] | undefined = useMemo(() => {
    if (!payeezyTokenList) {
      return
    }

    return paymentTokens.filter(token => payeezyTokenList?.[token.publicHash]).map(token => {
      const meta = parseCreditCardDetails(token)
      return {
        vault: token,
        payeezy: payeezyTokenList?.[token.publicHash] as PayeezyPaymentToken,
        meta
      }
    })
  }, [payeezyTokenList, paymentTokens])

  const setDefaultToken = useMemo(() => {
    return (publicHash: string, isDefault = true) => {
      const entityId = payeezyTokenList?.[publicHash]?.entity_id
      if (!entityId) {
        return
      }

      savePaymentToken({
        variables: {
          entityId,
          isDefault
        }
      }).then(() => {
        refetch()
        fetchPaymentTokens()
      })
    }
  }, [
    refetch,
    fetchPaymentTokens,
    payeezyTokenList,
    savePaymentToken
  ])

  const deleteToken = useMemo(() => {
    return (publicHash: string) => {
      const entityId = payeezyTokenList?.[publicHash]?.entity_id
      if (!entityId) {
        return
      }

      deletePaymentToken({
        variables: {
          entityId
        }
      }).then(() => {
        refetch()
        fetchPaymentTokens()
      })
    }
  }, [
    refetch,
    fetchPaymentTokens,
    payeezyTokenList,
    deletePaymentToken
  ])

  useEffect(() => {
    fetchPaymentTokens()
  }, [fetchPaymentTokens])

  return {
    payeezyTokens,
    payeezyTokenList,
    setDefaultToken,
    deleteToken,
    tokensLoading: vaultTokensLoading || tokensLoading,
    tokenIsSaving,
    tokenIsDeleting
  }
}
